import axios from 'axios';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { generateHeaders } from 'utils/APIUtils';
import { RootState } from '../index';
import { Reject } from '../Reject';
import { handleApiError } from '../handleApiError';
import { logout } from './auth';

export interface SpotDraft {
    id: string;
    idUser: string;
    step: 1 | 2 | 3 | 4;
    latitude?: number;
    longitude?: number;
    zoom?: number;
    name?: string;
    description?: string;
    selectedCategories?: string[];
    selectedHazards?: string[];
    pictureIds?: string[];
    youTubeLink?: string;
    privacy?: string;
    anonymity?: string;
    createdAt: string;
    updatedAt: string;
}

export type SpotDraftPayload = Omit<SpotDraft, 'id' | 'idUser' | 'createdAt' | 'updatedAt'>;

interface SpotDraftsState {
    drafts: SpotDraft[];
    loadingDrafts: boolean;
    savingDraft: boolean;
    activeDraftId: string | null;
    error: string | null;
}

const initialState: SpotDraftsState = {
    drafts: [],
    loadingDrafts: false,
    savingDraft: false,
    activeDraftId: null,
    error: null,
};

export const fetchDrafts = createAsyncThunk<SpotDraft[], void, Reject>(
    'spotDrafts/fetch',
    async (_, thunkAPI) => {
        const { auth } = thunkAPI.getState() as RootState;
        const headers = generateHeaders(auth.jwt);
        return axios
            .get<SpotDraft[]>(`${process.env.REACT_APP_API_URL}/spot-draft`, { headers })
            .then((r) => r.data)
            .catch((e) => thunkAPI.rejectWithValue(handleApiError(e)));
    }
);

export const createDraft = createAsyncThunk<SpotDraft, SpotDraftPayload, Reject>(
    'spotDrafts/create',
    async (payload, thunkAPI) => {
        const { auth } = thunkAPI.getState() as RootState;
        const headers = generateHeaders(auth.jwt);
        return axios
            .post<SpotDraft>(`${process.env.REACT_APP_API_URL}/spot-draft`, payload, { headers })
            .then((r) => r.data)
            .catch((e) => thunkAPI.rejectWithValue(handleApiError(e)));
    }
);

export const updateDraft = createAsyncThunk<SpotDraft, { id: string } & Partial<SpotDraftPayload>, Reject>(
    'spotDrafts/update',
    async ({ id, ...payload }, thunkAPI) => {
        const { auth } = thunkAPI.getState() as RootState;
        const headers = generateHeaders(auth.jwt);
        return axios
            .put<SpotDraft>(`${process.env.REACT_APP_API_URL}/spot-draft/${id}`, payload, { headers })
            .then((r) => r.data)
            .catch((e) => thunkAPI.rejectWithValue(handleApiError(e)));
    }
);

export const deleteDraft = createAsyncThunk<string, string, Reject>(
    'spotDrafts/delete',
    async (id, thunkAPI) => {
        const { auth } = thunkAPI.getState() as RootState;
        const headers = generateHeaders(auth.jwt);
        return axios
            .delete(`${process.env.REACT_APP_API_URL}/spot-draft/${id}`, { headers })
            .then(() => id)
            .catch((e) => thunkAPI.rejectWithValue(handleApiError(e)));
    }
);

interface StateWithSpotDrafts {
    spotDrafts: SpotDraftsState;
}

export const upsertDraft = createAsyncThunk<SpotDraft, SpotDraftPayload, Reject>(
    'spotDrafts/upsert',
    async (payload, thunkAPI) => {
        const state = thunkAPI.getState() as StateWithSpotDrafts;
        const activeDraftId = state.spotDrafts.activeDraftId;

        if (activeDraftId) {
            return thunkAPI.dispatch(updateDraft({ id: activeDraftId, ...payload })).unwrap();
        } else {
            return thunkAPI.dispatch(createDraft(payload)).unwrap();
        }
    }
);

const spotDraftsSlice = createSlice({
    name: 'spotDrafts',
    initialState,
    reducers: {
        setActiveDraftId(state, action: PayloadAction<string | null>) {
            state.activeDraftId = action.payload;
        },
        clearDrafts(state) {
            state.drafts = [];
            state.activeDraftId = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchDrafts.pending, (state) => { state.loadingDrafts = true; })
            .addCase(fetchDrafts.fulfilled, (state, action) => {
                state.loadingDrafts = false;
                state.drafts = action.payload;
            })
            .addCase(fetchDrafts.rejected, (state) => { state.loadingDrafts = false; })

            .addCase(createDraft.pending, (state) => { state.savingDraft = true; })
            .addCase(createDraft.fulfilled, (state, action) => {
                state.savingDraft = false;
                state.drafts.push(action.payload);
                state.activeDraftId = action.payload.id;
            })
            .addCase(createDraft.rejected, (state) => { state.savingDraft = false; })

            .addCase(updateDraft.pending, (state) => { state.savingDraft = true; })
            .addCase(updateDraft.fulfilled, (state, action) => {
                state.savingDraft = false;
                const idx = state.drafts.findIndex((d) => d.id === action.payload.id);
                if (idx !== -1) state.drafts[idx] = action.payload;
            })
            .addCase(updateDraft.rejected, (state) => { state.savingDraft = false; })

            .addCase(deleteDraft.fulfilled, (state, action) => {
                state.drafts = state.drafts.filter((d) => d.id !== action.payload);
                if (state.activeDraftId === action.payload) state.activeDraftId = null;
            })

            .addCase(logout.toString(), () => ({ ...initialState }));
    },
});

export const { setActiveDraftId, clearDrafts } = spotDraftsSlice.actions;
export default spotDraftsSlice.reducer;
